import React from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatUptime } from './eqpState';

dayjs.extend(relativeTime);

// 상태 칩: 사용중지가 가장 먼저(온/오프라인 무의미), 그다음 온라인/오프라인
const StatusChip = ({ row }) => {
  if (row.useYn === 'N') {
    return <Chip size="small" label="사용중지" variant="outlined" />;
  }
  if (row.online) {
    return <Chip size="small" label="온라인" color="success" />;
  }
  return <Chip size="small" label="오프라인" color="default" />;
};

// 전송 보안(PROTOCOL.md §6) - 기본 상태 칩 옆에 암호화 여부/보안 경고를 덧붙인다.
const SecurityChips = ({ row }) => (
  <>
    {row.transport === 'http-allowed' && (
      <Tooltip title="에이전트 설정에서 allow_http 로 http 를 강제 허용했습니다. 가능하면 https 주소를 쓰세요.">
        <Chip size="small" color="warning" variant="outlined" label="암호화 안 됨" />
      </Tooltip>
    )}
    {row.secWarn && (
      <Tooltip title={`${row.secWarn}${row.secWarnDt ? ` (${dayjs(row.secWarnDt).format('YYYY-MM-DD HH:mm:ss')})` : ''}`}>
        <Chip size="small" color="error" label="보안 경고" />
      </Tooltip>
    )}
  </>
);

const LastSeenCell = ({ value }) => {
  if (!value) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const d = dayjs(value);
  return (
    <Tooltip title={d.format('YYYY-MM-DD HH:mm:ss')}>
      <span>{d.fromNow()}</span>
    </Tooltip>
  );
};

const EqpAgentListPanel = ({ list, loading, onAdd, onRowClick, onOpenInstaller }) => {
  const columns = [
    { field: 'agentNm',  headerName: '이름',     flex: 1, minWidth: 140 },
    { field: 'location', headerName: '위치',     width: 130, renderCell: ({ value }) => value || '-' },
    {
      field: 'status', headerName: '상태', width: 220, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusChip row={row} />
          <SecurityChips row={row} />
        </Box>
      ),
    },
    {
      field: 'lastSeenDt', headerName: '마지막 접속', width: 140,
      renderCell: LastSeenCell,
    },
    {
      field: 'agentVer', headerName: '버전', width: 90,
      renderCell: ({ value }) => value || '-',
    },
    {
      field: 'drivers', headerName: '드라이버', width: 180, sortable: false,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(value || []).length === 0
            ? '-'
            : value.map((d) => (
              <Chip key={d.type} size="small" variant="outlined" label={`${d.type} v${d.ver}`} />
            ))}
        </Box>
      ),
    },
    {
      field: 'eqpCnt', headerName: '담당 장비', width: 90,
      renderCell: ({ value }) => value ?? 0,
    },
    {
      field: 'hostNm', headerName: 'PC 이름', width: 140,
      renderCell: ({ value }) => value || '-',
    },
    {
      // 쌓여 있으면 전송이 밀리고 있다는 뜻 - 100건 넘으면 눈에 띄게 경고색
      field: 'queueLen', headerName: '대기 큐', width: 90,
      renderCell: ({ value }) => (
        <Typography variant="body2" color={Number(value) > 100 ? 'error.main' : 'inherit'} fontWeight={Number(value) > 100 ? 700 : 400}>
          {value ?? 0}
        </Typography>
      ),
    },
    {
      field: 'lastSendErr', headerName: '최근 전송 오류', width: 170,
      renderCell: ({ row }) => (row.lastSendErr ? (
        <Tooltip title={row.lastSendErrDt ? dayjs(row.lastSendErrDt).format('YYYY-MM-DD HH:mm:ss') : ''}>
          <Typography variant="body2" color="error.main" noWrap>{row.lastSendErr}</Typography>
        </Tooltip>
      ) : '-'),
    },
    {
      field: 'uptimeSec', headerName: '가동시간', width: 100,
      renderCell: ({ value }) => formatUptime(value),
    },
    {
      field: 'statsDt', headerName: '상태 수신', width: 110,
      renderCell: ({ value }) => (value ? (
        <Tooltip title={dayjs(value).format('YYYY-MM-DD HH:mm:ss')}><span>{dayjs(value).fromNow()}</span></Tooltip>
      ) : '-'),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onOpenInstaller}>
            설치 파일 받기
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
            에이전트 등록
          </Button>
        </Box>
      </Box>

      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.agentUuid}
        loading={loading}
        autoHeight
        disableColumnMenu
        pageSizeOptions={[20, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        onRowClick={({ row }) => onRowClick(row)}
        sx={{
          cursor: 'pointer',
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
        }}
      />
    </Box>
  );
};

export default EqpAgentListPanel;
